import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { purchasesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, Search, Pencil, Trash2, Warehouse } from 'lucide-react';
import { toast } from 'sonner';

const PurchasesListPage = () => {
  const { user } = useAuth();
  const isSlave = user?.role === 'company_user';
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { fetchList(); }, []);

  const fetchList = async (q = '') => {
    try {
      const r = await purchasesApi.getAll({ search: q });
      setList(r.data);
    } catch (e) { toast.error('Errore caricamento acquisti'); }
    finally { setLoading(false); }
  };

  const onSearch = (e) => { setSearch(e.target.value); fetchList(e.target.value); };

  const handleDelete = async () => {
    try {
      await purchasesApi.delete(selected.id);
      toast.success('DDT Acquisto eliminato (stock ripristinato)');
      setDeleteDialog(false);
      fetchList(search);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore eliminazione'); }
  };

  const fmtCur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

  return (
    <div data-testid="purchases-list-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">DDT Acquisto</h1>
          <p className="text-muted-foreground text-sm">Carico magazzino dai fornitori — incrementa automaticamente lo stock</p>
        </div>
        <Link to="/acquisti/new">
          <Button data-testid="new-purchase-btn" className="btn-primary">
            <Plus className="w-4 h-4 mr-2" /> NUOVO DDT ACQUISTO
          </Button>
        </Link>
      </div>

      <Card className="card-swiss">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="search-purchase-input" placeholder="Cerca per numero o fornitore..." value={search} onChange={onSearch} className="pl-10" />
          </div>
        </CardContent>
      </Card>

      <Card className="card-swiss">
        <CardContent className="p-0">
          {loading ? <div className="p-8 text-center text-muted-foreground">Caricamento...</div> :
           list.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Warehouse className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessun DDT acquisto</p>
              <Link to="/acquisti/new">
                <Button className="mt-4 btn-primary">
                  <Plus className="w-4 h-4 mr-2" /> CREA IL PRIMO DDT ACQUISTO
                </Button>
              </Link>
            </div>
           ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header-swiss w-[120px]">N° Interno</TableHead>
                  <TableHead className="table-header-swiss">Fornitore</TableHead>
                  <TableHead className="table-header-swiss w-[130px]">Doc. Fornitore</TableHead>
                  <TableHead className="table-header-swiss w-[110px]">Data</TableHead>
                  <TableHead className="table-header-swiss w-[100px] text-right">Box</TableHead>
                  <TableHead className="table-header-swiss w-[130px] text-right">Totale</TableHead>
                  <TableHead className="table-header-swiss w-[110px]">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((p) => (
                  <TableRow key={p.id} className="table-row-swiss">
                    <TableCell className="table-cell-swiss font-['JetBrains_Mono'] font-medium">AC-{p.number}/{p.year}</TableCell>
                    <TableCell className="table-cell-swiss">{p.supplier_name || <span className="text-muted-foreground italic">—</span>}</TableCell>
                    <TableCell className="table-cell-swiss font-['JetBrains_Mono'] text-xs">{p.supplier_doc_number || '-'}</TableCell>
                    <TableCell className="table-cell-swiss">{fmtDate(p.date)}</TableCell>
                    <TableCell className="table-cell-swiss text-right font-['JetBrains_Mono']">{p.total_boxes}</TableCell>
                    <TableCell className="table-cell-swiss text-right font-['JetBrains_Mono']">{fmtCur(p.total_amount)}</TableCell>
                    <TableCell className="table-cell-swiss">
                      <div className="flex gap-1">
                        <Link to={`/acquisti/${p.id}/edit`}>
                          <Button data-testid={`edit-purchase-${p.id}`} variant="ghost" size="icon" className="h-8 w-8" title="Modifica">
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </Link>
                        {!isSlave && (
                          <Button data-testid={`delete-purchase-${p.id}`} variant="ghost" size="icon" onClick={() => { setSelected(p); setDeleteDialog(true); }} className="h-8 w-8 text-destructive" title="Elimina">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
           )}
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina DDT Acquisto</AlertDialogTitle>
            <AlertDialogDescription>
              Eliminando AC-{selected?.number}/{selected?.year} verrà <b>ripristinato lo stock</b>: le quantità precedentemente caricate saranno sottratte dal magazzino. L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-purchase-btn" onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Elimina e ripristina stock</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PurchasesListPage;
